import { Injectable, OnDestroy, inject } from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

/**
 * App-wide backstop for an expired Entra session.
 *
 * {@link import('./auth.guard').authGuard} only proves a live API token at *navigation* time. A tab left
 * open past the Entra inactivity timeout never navigates, so the guard never re-runs: the cached account
 * still sits in localStorage while the server session is gone, silent token renewal fails with
 * interaction_required, and the app becomes a zombie — GraphQL requests die token-less and SignalR
 * 401-loops. MSAL raises an ACQUIRE_TOKEN_FAILURE (or SSO_SILENT_FAILURE) event on every one of those
 * failed silent renewals, including the ones the MSAL interceptor runs for each API call.
 *
 * We listen for those events for the lifetime of the app (wired up at startup via provideAppInitializer in
 * main.ts, so it's active no matter which route the user is sitting on) and, when the failure is
 * interaction-required, hand off to {@link AuthService.expireSession} to log the user out to the login
 * screen. Transient, non-interaction failures (e.g. a network blip) are ignored — the interceptor retries
 * those on the next request, so we don't bounce the user for a hiccup.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryService implements OnDestroy {
  private readonly broadcast = inject(MsalBroadcastService, { optional: true });
  private readonly auth = inject(AuthService);
  private readonly destroying$ = new Subject<void>();

  init(): void {
    // Nothing to watch under mock auth (no MSAL broadcast service is provided there).
    if (environment.useMockAuth || !this.broadcast) {
      return;
    }

    this.broadcast.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
            msg.eventType === EventType.SSO_SILENT_FAILURE,
        ),
        takeUntil(this.destroying$),
      )
      .subscribe((msg: EventMessage) => {
        if (this.isInteractionRequired(msg.error)) {
          this.auth.expireSession();
        }
      });
  }

  /**
   * Whether a token-acquisition failure means the user must sign in interactively again (session gone)
   * rather than a recoverable/transient error. MSAL usually surfaces this as InteractionRequiredAuthError,
   * but we also match the well-known error codes defensively in case the error arrives as a plain object.
   */
  private isInteractionRequired(error: unknown): boolean {
    if (error instanceof InteractionRequiredAuthError) {
      return true;
    }
    const err = error as { errorCode?: string; message?: string } | null | undefined;
    const text = String(err?.errorCode ?? err?.message ?? '');
    return /interaction_required|login_required|consent_required|AADSTS160021|AADSTS50058|no_tokens_found|no_account_error/i.test(
      text,
    );
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}
